import { animate, motion, useMotionValue, useTransform } from "motion/react";
import {
  any,
  bool,
  elementType,
  func,
  number,
  object,
  string,
} from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";
import { glow } from "theme";

import ButtonSizer from "NavBar/AccountAndSettings/ButtonSizer";
import { useNavBarScrollTop } from "NavBar/navBarStore";
import { usePrimaryNavWidth } from "NavBar/stateHooks";
import { springAnimation } from "NavBar/styleConstants";

import FnrShineButton from "UI/FnrButton3/FnrShineButton";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrFade from "UI/FnrFade";
import FnrNumericIndicator from "UI/FnrNumericIndicator";
import FnrSlides from "UI/FnrSlides";
import FnrTypography from "UI/FnrTypography";
import { colors, navAccentColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";
import useOnClickOutside from "Utilities/Hooks/useOnClickOutside";
import useWidth from "Utilities/Hooks/useWidth";
import { useWindowSize } from "Utilities/Hooks/useWindowSize";
import dynBackgroundColor from "Utilities/interpolations/dynBackgroundColor";
import dynBorderRadius from "Utilities/interpolations/dynBorderRadius";
import dynLeft from "Utilities/interpolations/dynLeft";
import dynMinWidth from "Utilities/interpolations/dynMinWidth";
import dynPadding from "Utilities/interpolations/dynPadding";
import dynTop from "Utilities/interpolations/dynTop";
import flash from "Utilities/interpolations/flash";

import { typographyProps } from "../NavLink";

const iconConfig = [
  {
    key: "close",
    value: () => (
      <FnrContainedIcon
        shadow
        icon="times fas"
        size="xs"
        yCorrection={-2}
        color="textLight"
        style={{ WebkitTextStroke: "0.5px white" }}
      />
    ),
  },
  {
    key: "open",
    value: () => (
      <FnrContainedIcon
        shadow
        icon="cog fas"
        size="md"
        yCorrection={0}
        color="textLight"
      />
    ),
  },
];

const SettingsButton = ({
  count,
  navSectionKey,
  selected,
  Content,
  closeTrigger,
  settingsContentProps,
  directAction,
  settingsButtonRef,
  isSettingsFlashing,
  ...props
}) => {
  const localRef = useRef();
  const ref = settingsButtonRef || localRef;
  const contentsRef = useRef();
  const contentsWidth = useWidth(contentsRef);
  const navigationWidth = usePrimaryNavWidth();
  const { width: windowWidth } = useWindowSize();

  const translateXGoal = useMemo(() => {
    const offset = 40;
    return Math.min(
      windowWidth - (contentsWidth + navigationWidth) + offset,
      0
    );
  }, [contentsWidth, navigationWidth, windowWidth]);

  const translateXMotion = useMotionValue(0);
  const translateXTransform = useTransform(
    translateXMotion,
    (v) => `translateX(${v}px)`
  );

  useEffect(() => {
    const animation = animate(
      translateXMotion,
      translateXGoal,
      springAnimation
    );
    return () => animation.stop();
  }, [translateXMotion, translateXGoal]);

  const [showContent, setShowContent] = useState(false);

  const isHoverFocused = useIsHoverFocused(ref);

  const [xHasBeenClickedSinceLastHover, setXHasBeenClickedSinceLastHover] =
    useState(false);
  useEffect(() => {
    if (!isHoverFocused) setXHasBeenClickedSinceLastHover(false);
  }, [isHoverFocused]);

  const selectedColors = useMemo(() => {
    return navAccentColors[navSectionKey] || [];
  }, [navSectionKey]);

  const backgroundColor = useMemo(() => {
    if (selected || showContent || isHoverFocused) return selectedColors[1];
    return undefined;
  }, [isHoverFocused, selected, selectedColors, showContent]);

  const flashToColor = useMemo(
    () => addOpacity(selectedColors[1], 0.5),
    [selectedColors]
  );

  const handleClick = useCallback(() => {
    if (typeof directAction === "function") {
      directAction();
      if (typeof ref.current.onmouseout === "function")
        ref.current.onmouseout();
      return;
    }
    if (showContent) setXHasBeenClickedSinceLastHover(true);
    setShowContent(!showContent);
  }, [showContent, directAction]);

  const config = useMemo(
    () => [
      { key: "nothing", value: () => null },
      { key: "content", value: Content },
    ],
    [Content]
  );

  const showSettingsText = useMemo(
    () =>
      isHoverFocused &&
      !showContent &&
      !xHasBeenClickedSinceLastHover &&
      !(selected && directAction),
    [
      directAction,
      isHoverFocused,
      selected,
      showContent,
      xHasBeenClickedSinceLastHover,
    ]
  );

  const backgroundRef = useRef();

  useOnClickOutside(backgroundRef, (e) => {
    setShowContent(false);
    if (e.key === "Escape") ref.current.focus();
    else if (typeof ref.current.onmouseout === "function")
      ref.current.onmouseout();
  });

  useEffect(() => {
    setShowContent(false);
  }, [closeTrigger]);

  const navbarScrollTop = useNavBarScrollTop();
  const containerRef = useRef();
  const backgroundTop =
    navbarScrollTop > 0 && containerRef.current
      ? containerRef.current.getBoundingClientRect().top
      : "auto";

  return (
    <StaticWidth data-isopensettingsbutton={showContent} ref={containerRef}>
      <Background
        ref={backgroundRef}
        $backgroundColor={backgroundColor}
        $glowColor={selectedColors[0]}
        $noGlow={!(selected || showContent)}
        $padding={showContent ? "4px" : "0px"}
        $borderRadius={showContent ? "11px" : "18px"}
        $delayBackgroundChange={showContent}
        $showContent={showContent}
        style={{
          top: backgroundTop,
          transform: translateXTransform,
        }}
      >
        <DoNotPushDown>
          <SFnrShineButton
            {...props}
            ref={ref}
            onClick={handleClick}
            data-cy="NavSection_SettingsButton"
            $top={showContent ? -12 : 0}
            $left={showContent ? -12 : 0}
            $glowColor={selectedColors[0]}
            $noGlow={!(selected || showContent)}
            $backgroundColor={
              showContent ? selectedColors[1] : colors.transparent
            }
            $delayBackgroundChange={!showContent}
            $borderRadius={showContent ? "24px" : "18px"}
          >
            <TextAndButtonWrapper>
              <FlashingIconWrapper
                $isFlashing={isSettingsFlashing}
                $flashToColor={flashToColor}
              >
                <ButtonSizer
                  $marginRight={showSettingsText ? -4 : 0}
                  $size={showContent ? 24 : 36}
                >
                  <IconAndNumbercontainer>
                    <FnrSlides
                      activeKey={showContent ? "close" : "open"}
                      components={iconConfig}
                      variant="zoom"
                    />
                    <FnrNumericIndicator
                      count={count}
                      show={!showContent}
                      top={-2}
                      right={-2}
                      scale={0.55}
                    />
                  </IconAndNumbercontainer>
                </ButtonSizer>
              </FlashingIconWrapper>
              <FnrFade show={showSettingsText} top={false} left>
                <TextWrapper>
                  <FnrTypography {...typographyProps}>Settings</FnrTypography>
                </TextWrapper>
              </FnrFade>
            </TextAndButtonWrapper>
          </SFnrShineButton>
        </DoNotPushDown>
        <FnrSlides
          ref={contentsRef}
          variant="fade"
          activeKey={showContent ? "content" : "nothing"}
          components={config}
          {...settingsContentProps}
        />
      </Background>
    </StaticWidth>
  );
};

SettingsButton.propTypes = {
  count: number,
  showPopover: bool,
  navSectionKey: string,
  selected: bool,
  closeTrigger: any,
  Content: elementType,
  settingsContentProps: object,
  directAction: func,
  settingsButtonRef: object,
  isSettingsFlashing: bool,
};

const DoNotPushDown = styled.div`
  height: 0px;
`;

const TextWrapper = styled.div`
  margin-right: 12px;
  transform: translateY(-1.25px);
`;

const TextAndButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StaticWidth = styled.div`
  width: 36px;
  position: relative;
`;

const IconAndNumbercontainer = styled.div`
  position: relative;
`;

const FlashingIconWrapper = styled.div`
  border-radius: 50%;
  ${flash}
`;

const SFnrShineButton = styled(FnrShineButton)`
  position: relative;
  ${dynTop}
  ${dynLeft}
  ${dynBackgroundColor}
  ${dynBorderRadius}
  ${dynMinWidth}
  ${({ $glowColor }) => (!$glowColor ? "" : glow)}  
  transition: background-color ${({ $delayBackgroundChange }) =>
    $delayBackgroundChange
      ? "300ms 300ms" /* This change should happen while against a same colored background. */
      : "0ms"}, 
    top 300ms ease-in-out,
    left 300ms ease-in-out, border-radius 300ms ease-in-out;
  z-index: 21; // Putting this above the shine of menu buttons. Also further elevated to ensure that mouse events are trigger in the correct stacking order as well.
`;

const Background = styled(motion.div)`
  position: fixed;
  ${dynBorderRadius}
  ${dynBackgroundColor}
  ${({ $showContent }) =>
    $showContent ? "min-height: 32px;" : "min-height: 36px;"}
  ${({ $glowColor }) => (!$glowColor ? "" : glow)}
  ${dynPadding}
  z-index: 1;
  transition: min-height 300ms,
    background-color 300ms ease-in-out
      ${({ $delayBackgroundChange }) => ($delayBackgroundChange ? "100ms" : "")},
    padding 300ms ease-in-out, border-radius 300ms ease-in-out;
`;

export default SettingsButton;
